import { motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
// useReducedMotion used by SectionHead for reduced-motion handling

export const EASE = [0.22, 1, 0.36, 1];

/** Type A dark→dark section transition - subtle 1px hairline. */
export function SectionRule() {
  return <div className="section-rule" aria-hidden="true" />;
}

export function MaskedLines({ lines, className = "", lineClassName = "", delay = 0, stagger = 0.14, immediate = false }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const inView = useInView(ref, { once: true, margin: "-12% 0px" });
  const show = immediate || inView;

  if (reduced) {
    return (
      <span ref={ref} className={`block ${className}`}>
        {lines.map((line, i) => (
          <span key={i} className={`block ${lineClassName}`}>
            {line}
          </span>
        ))}
      </span>
    );
  }

  return (
    <span ref={ref} className={`block ${className}`}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            initial={{ y: "112%" }}
            animate={show ? { y: "0%" } : {}}
            transition={{ duration: 0.9, ease: EASE, delay: delay + i * stagger }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export function Reveal({ children, delay = 0, y = 28, className = "", duration = 0.8, reduced = false }) {
  if (reduced) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

export function SectionHead({ index, eyebrow, titleLines, copy, className = "", compact = false }) {
  const reduced = useReducedMotion();
  return (
    <div className={className}>
      <Reveal reduced={reduced}>
        <Eyebrow index={index}>{eyebrow}</Eyebrow>
      </Reveal>
      <MaskedLines
        lines={titleLines}
        className={`font-display text-4xl font-semibold leading-[1.04] tracking-tight text-slate-50 sm:text-5xl ${
          compact ? "mt-3" : "mt-5"
        }`}
        delay={0.08}
        immediate={reduced}
      />
      {copy ? (
        <Reveal delay={0.25} reduced={reduced}>
          <p className={`body-copy max-w-[38ch] sm:max-w-[46ch] ${compact ? "mt-3" : "mt-5"}`}>{copy}</p>
        </Reveal>
      ) : null}
    </div>
  );
}

export function Eyebrow({ index, children, className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="section-eyebrow">
      <span className="h-1.5 w-1.5 rotate-45 bg-teal shadow-[0_0_12px_rgba(0,245,212,0.8)]" />
      <span className="eyebrow-label">
        {index ? <span className="mr-3 text-slate-500">{index}</span> : null}
        {children}
      </span>
    </div>
  );
}

export function CountUp({ to, decimals = 0, prefix = "", suffix = "", duration = 1.8, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-8% 0px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVal(to);
      return undefined;
    }
    let start;
    let frame;
    const from = 0;
    const startTime = performance.now();
    const tick = (now) => {
      if (!start) start = now;
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (to - from) * eased);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, to, duration]);

  const formatted = val.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

export function TiltCard({ children, className = "", max = 2 }) {
  // Lightweight tilt without full spring setup for Phase 1/2
  return (
    <motion.div
      className={`relative ${className}`}
      style={{ perspective: 900 }}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
    >
      {children}
    </motion.div>
  );
}
